<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import DealProductsAPI from 'dashboard/api/pipelines/dealProducts';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import AddAttribute from 'dashboard/routes/dashboard/settings/attributes/AddAttribute.vue';
import AddLabel from 'dashboard/routes/dashboard/settings/labels/AddLabel.vue';
import DealModal from 'dashboard/components/pipelines/DealModal.vue';
import { fetchContactsListWithCache } from 'dashboard/helper/contactDetailsCache';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';

const route = useRoute();
const store = useStore();
const { t } = useI18n();
const confirmDealDeleteModal = ref(null);
const DOCK_STATE_KEY = '__cwContactDealsDockState';
const stageUpdatingDealId = ref(null);
const initialLoadDone = ref(false);
const activeDealPriorityPopoverId = ref(null);
const dealPriorityPopoverStyle = ref({ top: '120px', left: '24px' });
const activeDealActionsId = ref(null);
const activeDealLabelsPopoverId = ref(null);
const activeDealCustomFieldsPopoverId = ref(null);
const dealCustomFieldsPopoverStyle = ref({ top: '120px', left: '24px' });
const dealCustomFieldsSearch = ref('');
const prioritySaving = ref(false);
const labelsSaving = ref(false);
const dealLabelsSearch = ref('');
const showAddLabelModal = ref(false);
const showCreateDealModal = ref(false);
const selectedDealForForm = ref(null);
const creatingDeal = ref(false);
const selectedPipelineId = ref(null);
const selectedStageId = ref(null);
const newDealTitle = ref('');
const newDealDescription = ref('');
const newDealStartDate = ref('');
const newDealCloseDate = ref('');
const selectedDealContactId = ref(null);
const selectedDealConversationId = ref(null);
const selectedDealAgentIds = ref([]);
const selectedDealLabels = ref([]);
const showCreateProductsPopover = ref(false);
const createProductsView = ref('summary');
const createProductSearch = ref('');
const createSelectedProducts = ref([]);
const createCouponCode = ref('');
const createSelectedCoupon = ref(null);
const createManualDiscount = ref('0');
const createSurcharge = ref('0');
const createShipping = ref('0');
const showCreateProductForm = ref(false);
const newProduct = ref({ name: '', sku: '', price: '' });
const showDealCustomFieldsModal = ref(false);
const showAddDealAttributeModal = ref(false);
const activeDealPopoverId = ref(null);
const dealProductsView = ref('summary');
const dealProductSearch = ref('');
const showCreateDealProductForm = ref(false);
const dealNewProduct = ref({ name: '', sku: '', price: '' });
const dealSelectedProducts = ref([]);
const dealPopoverStyle = ref({ top: '120px', left: '24px' });
const dealCouponCode = ref('');
const dealManualDiscount = ref('0');
const dealSurcharge = ref('0');
const dealShipping = ref('0');
const dealSelectedCoupon = ref(null);
const dealPopoverSaving = ref(false);
const selectedDealForCustomFields = ref(null);
const selectedCustomAttributeKey = ref(null);
const dealCustomFieldsDraft = ref({});
const dealCustomFieldKeys = ref([]);
const customFieldsSaving = ref(false);
const activeCustomFieldListKey = ref(null);
const customFieldListSearch = ref('');
const customFieldListPopoverStyle = ref({ top: '120px', left: '24px' });
const customFieldListPopoverWidth = ref(320);
const showConversationDock = ref(false);
const dockConversationId = ref(null);
const dockConversationInboxId = ref(0);
const isDockSidebarOpen = ref(false);
const activeAssigneePopoverDealId = ref(null);
const assigneePopoverStyle = ref({ top: '120px', left: '24px' });
const assigneeSelection = ref([]);
const assigneeSearch = ref('');
const assigneeSaving = ref(false);
const editingDealId = ref(null);
const showLoseDealModal = ref(false);
const loseDealLoading = ref(false);
const selectedDealForLoss = ref(null);
const selectedLossReasonId = ref(null);
const lossJustification = ref('');

const contactId = computed(() => Number(route.params.contactId));
const accountId = computed(() => Number(route.params.accountId));

const dealsUIFlags = useMapGetter('deals/getUIFlags');
const pipelinesGetter = useMapGetter('pipelines/getPipelines');
const labelsGetter = useMapGetter('labels/getLabels');
const productsGetter = useMapGetter('products/getProducts');
const couponsGetter = useMapGetter('coupons/getCoupons');
const agentsGetter = useMapGetter('agents/getAgents');
const lossReasonsGetter = useMapGetter('dealLossReasons/getLossReasons');
const allConversationsGetter = useMapGetter('getAllConversations');
const selectedChatGetter = useMapGetter('getSelectedChat');

const isLoading = computed(
  () => !initialLoadDone.value && dealsUIFlags.value?.isFetching
);
const pipelines = computed(() => pipelinesGetter.value || []);
const labels = computed(() => labelsGetter.value || []);
const products = computed(() => productsGetter.value || []);
const coupons = computed(() => couponsGetter.value || []);
const agents = computed(() => agentsGetter.value || []);
const lossReasons = computed(() => lossReasonsGetter.value || []);
const allConversations = computed(() => allConversationsGetter.value || []);
const currentDockChat = computed(() => selectedChatGetter.value || {});
const contacts = computed(() => store.getters['contacts/getContacts'] || []);
const currentContact = computed(() => {
  return (
    contacts.value.find(item => Number(item.id) === Number(contactId.value)) ||
    null
  );
});
const createDealInitialValues = computed(() => ({
  contact_id: contactId.value || null,
  contact_label:
    currentContact.value?.name ||
    currentContact.value?.email ||
    `Contato #${contactId.value}`,
}));
const dealAttributeDefinitions = computed(() => {
  const getter = store.getters['attributes/getAttributesByModel'];
  if (typeof getter !== 'function') return [];
  return getter('deal_attribute') || [];
});
const activeProducts = computed(() =>
  products.value.filter(product => product.active !== false)
);

const pipelineById = computed(() => {
  return Object.fromEntries(
    pipelines.value.map(pipeline => [Number(pipeline.id), pipeline])
  );
});

const selectedPipeline = computed(() =>
  pipelines.value.find(
    item => Number(item.id) === Number(selectedPipelineId.value)
  )
);

const stageOptions = computed(() =>
  (selectedPipeline.value?.stages || [])
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map(stage => ({
      label: stage.name,
      value: stage.id,
    }))
);
const contactDeals = computed(() => {
  const contextGetter = store.getters['deals/getDealsByContext'];
  const items =
    typeof contextGetter === 'function'
      ? contextGetter({
          accountId: accountId.value,
          params: { contact_id: contactId.value },
        })
      : store.getters['deals/getDeals'] || [];
  return items
    .filter(item => Number(item.contact_id) === contactId.value)
    .sort((a, b) => {
      const aDate = new Date(a.created_at || 0).getTime();
      const bDate = new Date(b.created_at || 0).getTime();
      return bDate - aDate;
    });
});

const getStageList = deal => {
  const stages = pipelineById.value[Number(deal.pipeline_id)]?.stages || [];
  return [...stages].sort((a, b) => (a.position || 0) - (b.position || 0));
};

const getPipelineName = deal => {
  return (
    pipelineById.value[Number(deal.pipeline_id)]?.name ||
    t('CONTACT_DEALS.NO_PIPELINE')
  );
};

const getDealAssignees = deal => {
  const customIds = Array.isArray(
    deal?.custom_attributes?.responsible_agent_ids
  )
    ? deal.custom_attributes.responsible_agent_ids.map(id => Number(id))
    : [];
  const nativeId = Number(deal?.assignee?.id || deal?.assignee_id);
  const ids = [
    ...new Set([...customIds, nativeId].filter(id => !Number.isNaN(id))),
  ];
  if (!ids.length && deal?.assignee) return [deal.assignee];
  return agents.value.filter(agent => ids.includes(Number(agent.id)));
};

const isEditingMode = computed(() => Boolean(editingDealId.value));

const getDealTitle = deal => {
  const normalizedTitle = (deal?.title || '').trim();
  if (normalizedTitle) return normalizedTitle;
  return t('CONTACT_DEALS.UNTITLED');
};

const formatCurrency = (amount, currency) => {
  const numericAmount = Number(amount || 0);
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL',
      minimumFractionDigits: 2,
    }).format(numericAmount);
  } catch (error) {
    return `R$ ${numericAmount.toFixed(2)}`;
  }
};

const getDealDisplayAmount = deal => {
  const amount = Number(deal?.amount);
  if (Number.isFinite(amount) && amount > 0) return amount;

  const calculated = Number(deal?.custom_attributes?.calculated_total);
  if (Number.isFinite(calculated) && calculated > 0) return calculated;

  const fallbackFromProducts = (deal?.products || []).reduce((sum, item) => {
    const quantity = Number(item?.quantity || 0);
    const unitPrice = Number(item?.unit_price || 0);
    if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) return sum;
    return sum + quantity * unitPrice;
  }, 0);

  return fallbackFromProducts > 0 ? fallbackFromProducts : 0;
};

const getDaysSince = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '0d';
  const diff = Date.now() - date.getTime();
  return `${Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)))}d`;
};

const formatDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR');
};

const formatDealCreatedAt = value => {
  if (!value) return '-';
  const createdAt = new Date(value);
  if (Number.isNaN(createdAt.getTime())) return '-';

  const diffMs = Date.now() - createdAt.getTime();
  if (diffMs >= 0 && diffMs < 24 * 60 * 60 * 1000) {
    if (diffMs < 60 * 60 * 1000) {
      return `${Math.max(1, Math.floor(diffMs / (60 * 1000)))}min`;
    }
    return `${Math.max(1, Math.floor(diffMs / (60 * 60 * 1000)))}h`;
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
  }).format(createdAt);
};

const getStageProgressStyle = (stage, index, deal) => {
  const stages = getStageList(deal);
  const currentIndex = stages.findIndex(
    item => Number(item.id) === Number(deal.pipeline_stage_id)
  );
  if (currentIndex === -1) return { backgroundColor: '#E2E8F0' };
  if (index <= currentIndex) {
    return { backgroundColor: stage.color || '#6B7280' };
  }
  return { backgroundColor: '#E2E8F0' };
};

const getStageTooltip = (stage, deal) => {
  const isCurrent = Number(stage.id) === Number(deal.pipeline_stage_id);
  return isCurrent
    ? `${stage.name} (${t('CONTACT_DEALS.STAGE_CURRENT')})`
    : stage.name;
};

const PRIORITY_OPTIONS = [
  {
    value: 'urgent',
    icon: 'i-lucide-chevrons-up',
    color: '#dc2626',
    label: 'Urgente',
  },
  {
    value: 'high',
    icon: 'i-lucide-chevron-up',
    color: '#eab308',
    label: 'Alta',
  },
  { value: 'medium', icon: 'i-lucide-equal', color: '#3b82f6', label: 'Media' },
  {
    value: 'low',
    icon: 'i-lucide-chevron-down',
    color: '#64748b',
    label: 'Baixa',
  },
  {
    value: 'none',
    icon: 'i-lucide-minus',
    color: '#64748b',
    label: 'Sem prioridade',
  },
];

const getDealPriorityValue = deal => {
  const priority = deal?.priority || deal?.custom_attributes?.priority;
  const exists = PRIORITY_OPTIONS.some(item => item.value === priority);
  return exists ? priority : 'none';
};

const getDealPriorityMeta = deal => {
  const value = getDealPriorityValue(deal);
  return (
    PRIORITY_OPTIONS.find(item => item.value === value) || PRIORITY_OPTIONS[4]
  );
};

const RESERVED_DEAL_CUSTOM_ATTRIBUTE_KEYS = new Set([
  'subtotal_products',
  'coupon_code',
  'coupon_discount',
  'manual_discount',
  'surcharge',
  'shipping',
  'calculated_total',
]);

const isCustomFieldValuePresent = value => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return true;
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const formatCustomFieldValue = value => {
  if (typeof value === 'boolean') return value ? 'Sim' : 'Nao';
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const getDealCustomFieldsEntries = deal => {
  const attrs = deal?.custom_attributes || {};
  const definitionsByKey = new Map(
    dealAttributeDefinitions.value.map(item => [item.attribute_key, item])
  );

  return Object.entries(attrs)
    .filter(([key, value]) => {
      if (RESERVED_DEAL_CUSTOM_ATTRIBUTE_KEYS.has(key)) return false;
      if (!definitionsByKey.has(key)) return false;
      return isCustomFieldValuePresent(value);
    })
    .map(([key, value]) => {
      const definition = definitionsByKey.get(key);
      return {
        key,
        label: definition.attribute_display_name,
        value,
        formattedValue: formatCustomFieldValue(value),
      };
    });
};

const getDealCustomFieldsCount = deal =>
  getDealCustomFieldsEntries(deal).length;

const getDealCustomFieldsForPopover = deal => {
  const term = dealCustomFieldsSearch.value.trim().toLowerCase();
  const entries = getDealCustomFieldsEntries(deal);
  if (!term) return entries;
  return entries.filter(item => {
    return (
      item.label?.toLowerCase().includes(term) ||
      item.formattedValue?.toLowerCase().includes(term)
    );
  });
};

const getDealLabels = deal => {
  const source = deal?.labels || deal?.label_list || [];
  if (Array.isArray(source)) return source;
  if (typeof source === 'string') {
    return source
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
};

const MAX_VISIBLE_DEAL_LABELS = 6;

const getVisibleDealLabels = deal =>
  getDealLabels(deal).slice(0, MAX_VISIBLE_DEAL_LABELS);

const getHiddenDealLabelsCount = deal =>
  Math.max(0, getDealLabels(deal).length - MAX_VISIBLE_DEAL_LABELS);

const getLabelColorByTitle = title => {
  const label = labels.value.find(item => item.title === title);
  return label?.color || '#64748B';
};

const filteredLabels = computed(() => {
  const term = dealLabelsSearch.value.trim().toLowerCase();
  if (!term) return labels.value;
  return labels.value.filter(item => item.title?.toLowerCase().includes(term));
});

const filteredAssigneeOptions = computed(() => {
  const term = assigneeSearch.value.trim().toLowerCase();
  if (!term) return agents.value;
  return agents.value.filter(agent => agent.name?.toLowerCase().includes(term));
});

const lossReasonOptions = computed(() =>
  lossReasons.value.map(item => ({
    label: item.name,
    value: item.id,
  }))
);

const selectedLossReason = computed(() => {
  return (
    lossReasons.value.find(
      item => Number(item.id) === Number(selectedLossReasonId.value)
    ) || null
  );
});
const getDealConversationRouteId = deal =>
  Number(deal?.conversation_display_id || deal?.conversation_id || 0);
const filteredDealCatalogProducts = computed(() => {
  const term = dealProductSearch.value.trim().toLowerCase();
  const selectedIds = dealSelectedProducts.value.map(item =>
    Number(item.product_id)
  );

  return activeProducts.value.filter(product => {
    if (selectedIds.includes(Number(product.id))) return false;
    if (!term) return true;
    return (
      product.name?.toLowerCase().includes(term) ||
      product.sku?.toLowerCase().includes(term)
    );
  });
});

const dealAttributeDefinitionOptions = computed(() => {
  const used = new Set(dealCustomFieldKeys.value);
  return dealAttributeDefinitions.value
    .filter(attribute => !used.has(attribute.attribute_key))
    .map(attribute => ({
      label: attribute.attribute_display_name,
      value: attribute.attribute_key,
    }));
});

const getDealAttributeDefinition = key =>
  dealAttributeDefinitions.value.find(item => item.attribute_key === key);

const getDraftCustomFieldType = key =>
  getDealAttributeDefinition(key)?.attribute_display_type || 'text';

const getDraftCustomFieldLabel = key => {
  return getDealAttributeDefinition(key)?.attribute_display_name || key;
};

const getDraftCustomFieldOptions = key => {
  return getDealAttributeDefinition(key)?.attribute_values || [];
};

const getDraftCustomFieldSelectedLabel = key => {
  const value = dealCustomFieldsDraft.value[key];
  if (!value)
    return t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.FIELD_PLACEHOLDER');
  return String(value);
};

const getFilteredDraftCustomFieldOptions = key => {
  const term = customFieldListSearch.value.trim().toLowerCase();
  const options = getDraftCustomFieldOptions(key);
  if (!term) return options;
  return options.filter(option => option.toLowerCase().includes(term));
};
const getProductSummary = deal => {
  const dealProducts = deal.products || [];
  if (!dealProducts.length) return t('DEALS.CONVERSATION.PRODUCTS.EMPTY');
  return `${dealProducts.length} ${t('DEALS.CONVERSATION.PRODUCTS.DEFAULT_NAME')}(s)`;
};
const dealSubtotalProducts = computed(() => {
  return dealSelectedProducts.value.reduce((total, item) => {
    return total + item.quantity * Number(item.unit_price || 0);
  }, 0);
});
const dealParsedManualDiscount = computed(() =>
  Number(dealManualDiscount.value || 0)
);
const dealParsedSurcharge = computed(() => Number(dealSurcharge.value || 0));
const dealParsedShipping = computed(() => Number(dealShipping.value || 0));
const dealCouponDiscount = computed(() => {
  if (!dealSelectedCoupon.value) return 0;
  const coupon = dealSelectedCoupon.value;
  if (coupon.discount_type === 'percentage') {
    return (dealSubtotalProducts.value * Number(coupon.value || 0)) / 100;
  }
  return Number(coupon.value || 0);
});
const dealEffectiveDiscount = computed(() => {
  if (dealParsedManualDiscount.value > 0) return dealParsedManualDiscount.value;
  return dealCouponDiscount.value;
});
const dealFinalTotal = computed(() => {
  const total =
    dealSubtotalProducts.value -
    dealEffectiveDiscount.value +
    dealParsedSurcharge.value +
    dealParsedShipping.value;
  return total > 0 ? total : 0;
});

const setDealPriorityPopoverPosition = event => {
  const trigger = event?.currentTarget;
  if (!trigger) return;
  const rect = trigger.getBoundingClientRect();
  const width = 230;
  const preferredHeight = 260;
  const margin = 12;
  const offset = 6;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const availableBelow = viewportHeight - rect.bottom - offset - margin;
  const availableAbove = rect.top - offset - margin;
  const openUpwards = availableBelow < 220 && availableAbove > availableBelow;
  const left = Math.max(
    margin,
    Math.min(rect.right - width, viewportWidth - width - margin)
  );
  const anchorTop = openUpwards ? rect.top - offset : rect.bottom + offset;
  const maxHeight = Math.max(
    180,
    Math.min(preferredHeight, openUpwards ? availableAbove : availableBelow)
  );
  dealPriorityPopoverStyle.value = {
    top: `${Math.max(margin, anchorTop)}px`,
    left: `${left}px`,
    maxHeight: `${Math.floor(maxHeight)}px`,
    transform: openUpwards ? 'translateY(-100%)' : 'none',
    transformOrigin: openUpwards ? 'bottom right' : 'top right',
  };
};

const setSmallPopoverPosition = (
  event,
  {
    width = 300,
    preferredHeight = 320,
    minHeight = 220,
    openThreshold = 240,
    align = 'end',
  } = {}
) => {
  const trigger = event?.currentTarget;
  if (!trigger) return { top: '120px', left: '24px' };
  const rect = trigger.getBoundingClientRect();
  const offset = 6;
  const margin = 12;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const availableBelow = viewportHeight - rect.bottom - offset - margin;
  const availableAbove = rect.top - offset - margin;
  const openUpwards =
    availableBelow < openThreshold && availableAbove > availableBelow;
  const maxHeight = Math.max(
    minHeight,
    Math.min(preferredHeight, openUpwards ? availableAbove : availableBelow)
  );
  const desiredLeft = align === 'start' ? rect.left : rect.right - width;
  const left = Math.max(
    margin,
    Math.min(desiredLeft, viewportWidth - width - margin)
  );
  const anchorTop = openUpwards ? rect.top - offset : rect.bottom + offset;
  return {
    top: `${Math.max(margin, anchorTop)}px`,
    left: `${left}px`,
    maxHeight: `${Math.floor(maxHeight)}px`,
    transform: openUpwards ? 'translateY(-100%)' : 'none',
    transformOrigin: openUpwards ? 'bottom right' : 'top right',
  };
};

const setAssigneePopoverPosition = event => {
  assigneePopoverStyle.value = setSmallPopoverPosition(event, {
    width: 320,
    preferredHeight: 360,
    minHeight: 220,
    openThreshold: 260,
    align: 'start',
  });
};

const toggleDealActions = dealId => {
  activeDealPriorityPopoverId.value = null;
  activeDealLabelsPopoverId.value = null;
  activeDealCustomFieldsPopoverId.value = null;
  activeDealPopoverId.value = null;
  activeAssigneePopoverDealId.value = null;
  activeDealActionsId.value =
    activeDealActionsId.value === dealId ? null : dealId;
};

const toggleDealPriorityPopover = (dealId, event) => {
  activeDealActionsId.value = null;
  activeDealLabelsPopoverId.value = null;
  activeDealCustomFieldsPopoverId.value = null;
  activeDealPopoverId.value = null;
  activeAssigneePopoverDealId.value = null;
  setDealPriorityPopoverPosition(event);
  activeDealPriorityPopoverId.value =
    activeDealPriorityPopoverId.value === dealId ? null : dealId;
};

const setDealPriority = async (deal, priority) => {
  prioritySaving.value = true;
  try {
    const nextPriority = priority === 'none' ? null : priority;
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: deal.id,
      deal: { priority: nextPriority },
    });
    activeDealPriorityPopoverId.value = null;
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || 'Erro ao atualizar prioridade'
    );
  } finally {
    prioritySaving.value = false;
  }
};

const toggleDealLabelsPopover = dealId => {
  activeDealActionsId.value = null;
  activeDealPriorityPopoverId.value = null;
  activeDealCustomFieldsPopoverId.value = null;
  activeDealPopoverId.value = null;
  activeAssigneePopoverDealId.value = null;
  dealLabelsSearch.value = '';
  activeDealLabelsPopoverId.value =
    activeDealLabelsPopoverId.value === dealId ? null : dealId;
};

const setDealPopoverPosition = event => {
  const trigger = event?.currentTarget;
  if (!trigger) return;
  const rect = trigger.getBoundingClientRect();
  const popoverWidth = 380;
  const preferredHeight = 560;
  const margin = 12;
  const offset = 8;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const left = Math.max(
    margin,
    Math.min(rect.left, viewportWidth - popoverWidth - margin)
  );
  let top = rect.bottom + offset;
  let maxHeight = Math.min(preferredHeight, viewportHeight - top - margin);

  if (maxHeight < 300) {
    const topWhenUp = Math.max(margin, rect.top - preferredHeight - offset);
    top = topWhenUp;
    maxHeight = Math.min(preferredHeight, viewportHeight - topWhenUp - margin);
  }

  dealPopoverStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
    maxHeight: `${Math.max(260, Math.floor(maxHeight))}px`,
  };
};

const setDealCustomFieldsPopoverPosition = event => {
  dealCustomFieldsPopoverStyle.value = setSmallPopoverPosition(event, {
    width: 360,
    preferredHeight: 420,
    minHeight: 220,
    openThreshold: 280,
    align: 'start',
  });
};

const toggleDealCustomFieldsPopover = (deal, event) => {
  if (activeDealCustomFieldsPopoverId.value === deal.id) {
    activeDealCustomFieldsPopoverId.value = null;
    return;
  }
  activeDealActionsId.value = null;
  activeDealPriorityPopoverId.value = null;
  activeDealLabelsPopoverId.value = null;
  activeDealPopoverId.value = null;
  activeAssigneePopoverDealId.value = null;
  setDealCustomFieldsPopoverPosition(event);
  dealCustomFieldsSearch.value = '';
  activeDealCustomFieldsPopoverId.value = deal.id;
};

const closeDealCustomFieldsPopover = () => {
  activeDealCustomFieldsPopoverId.value = null;
};

const closeDealProductsPopover = () => {
  activeDealPopoverId.value = null;
  showCreateDealProductForm.value = false;
  dealNewProduct.value = { name: '', sku: '', price: '' };
};

const openDealProductsPopover = (deal, event) => {
  if (activeDealPopoverId.value === deal.id) {
    closeDealProductsPopover();
    return;
  }
  setDealPopoverPosition(event);
  activeDealActionsId.value = null;
  activeDealPriorityPopoverId.value = null;
  activeDealLabelsPopoverId.value = null;
  activeDealCustomFieldsPopoverId.value = null;
  activeAssigneePopoverDealId.value = null;
  activeDealPopoverId.value = deal.id;
  dealProductsView.value = 'summary';
  dealProductSearch.value = '';
  showCreateDealProductForm.value = false;
  dealNewProduct.value = { name: '', sku: '', price: '' };

  const productsFromDeal = deal.products || [];
  dealSelectedProducts.value = productsFromDeal.map(item => ({
    deal_product_id: item.id || null,
    product_id: item.product_id,
    name: item.name || item.product?.name || 'Produto',
    sku: item.sku || item.product?.sku || '',
    quantity: Number(item.quantity || 1),
    unit_price: Number(item.unit_price || 0),
  }));

  const attrs = deal.custom_attributes || {};
  dealCouponCode.value = attrs.coupon_code || '';
  dealManualDiscount.value = String(attrs.manual_discount || 0);
  dealSurcharge.value = String(attrs.surcharge || 0);
  dealShipping.value = String(attrs.shipping || 0);
  dealSelectedCoupon.value = null;

  if (dealCouponCode.value) {
    const normalized = dealCouponCode.value.toUpperCase();
    dealSelectedCoupon.value = coupons.value.find(
      item => item.active && item.code?.toUpperCase() === normalized
    );
  }
};

const syncDealProducts = async ({
  dealId,
  existingProducts,
  selectedItems,
}) => {
  const normalizedExisting = (existingProducts || []).map(item => ({
    deal_product_id: item.id,
    product_id: item.product_id,
    quantity: Number(item.quantity || 1),
    unit_price: Number(item.unit_price || 0),
  }));
  const selectedByProduct = new Map(
    selectedItems.map(item => [Number(item.product_id), item])
  );
  const existingByProduct = new Map(
    normalizedExisting.map(item => [Number(item.product_id), item])
  );
  const deletes = normalizedExisting
    .filter(item => !selectedByProduct.has(Number(item.product_id)))
    .map(item =>
      DealProductsAPI.delete(accountId.value, dealId, item.deal_product_id)
    );
  const updates = normalizedExisting
    .filter(item => selectedByProduct.has(Number(item.product_id)))
    .filter(item => {
      const selected = selectedByProduct.get(Number(item.product_id));
      return (
        Number(selected.quantity) !== Number(item.quantity) ||
        Number(selected.unit_price) !== Number(item.unit_price)
      );
    })
    .map(item => {
      const selected = selectedByProduct.get(Number(item.product_id));
      return DealProductsAPI.update(
        accountId.value,
        dealId,
        item.deal_product_id,
        {
          quantity: Number(selected.quantity),
          unit_price: Number(selected.unit_price),
        }
      );
    });
  const creates = selectedItems
    .filter(item => !existingByProduct.has(Number(item.product_id)))
    .map(item =>
      DealProductsAPI.create(accountId.value, dealId, {
        product_id: item.product_id,
        quantity: Number(item.quantity),
        unit_price: Number(item.unit_price),
      })
    );

  await Promise.all([...deletes, ...updates, ...creates]);
};

const addProductToDealPopover = product => {
  dealSelectedProducts.value.push({
    deal_product_id: null,
    product_id: product.id,
    name: product.name,
    sku: product.sku,
    quantity: 1,
    unit_price: Number(product.price || 0),
  });
  dealProductsView.value = 'summary';
};

const increaseDealQty = item => {
  item.quantity += 1;
};

const removeDealProduct = productId => {
  dealSelectedProducts.value = dealSelectedProducts.value.filter(
    item => Number(item.product_id) !== Number(productId)
  );
};

const decreaseDealQty = item => {
  if (item.quantity <= 1) {
    removeDealProduct(item.product_id);
    return;
  }
  item.quantity -= 1;
};

const applyDealCoupon = () => {
  const normalized = dealCouponCode.value.trim().toUpperCase();
  if (!normalized) {
    dealSelectedCoupon.value = null;
    return;
  }

  const coupon = coupons.value.find(
    item => item.active && item.code?.toUpperCase() === normalized
  );
  if (!coupon) {
    dealSelectedCoupon.value = null;
    useAlert(t('DEALS.CONVERSATION.PRODUCTS.COUPON_NOT_FOUND'));
    return;
  }

  dealSelectedCoupon.value = coupon;
  dealManualDiscount.value = '0';
};

const createDealProductQuickly = async () => {
  if (!dealNewProduct.value.name || !dealNewProduct.value.sku) {
    useAlert(t('DEALS.CONVERSATION.PRODUCTS.FILL_NAME_SKU_ERROR'));
    return;
  }

  try {
    await store.dispatch('products/create', {
      accountId: accountId.value,
      product: {
        name: dealNewProduct.value.name,
        sku: dealNewProduct.value.sku,
        price: Number(dealNewProduct.value.price || 0),
        active: true,
      },
    });
    await store.dispatch('products/get', { accountId: accountId.value });
    showCreateDealProductForm.value = false;
    dealNewProduct.value = { name: '', sku: '', price: '' };
  } catch (error) {
    const message =
      error?.response?.data?.errors?.[0] ||
      t('DEALS.CONVERSATION.PRODUCTS.ERROR_CREATE_PRODUCT');
    useAlert(message);
  }
};

const saveDealProducts = async deal => {
  dealPopoverSaving.value = true;
  try {
    await syncDealProducts({
      dealId: deal.id,
      existingProducts: deal.products || [],
      selectedItems: dealSelectedProducts.value,
    });

    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: deal.id,
      deal: {
        amount: dealFinalTotal.value,
        custom_attributes: {
          ...(deal.custom_attributes || {}),
          subtotal_products: dealSubtotalProducts.value,
          coupon_code: dealSelectedCoupon.value?.code || null,
          coupon_discount: dealCouponDiscount.value,
          manual_discount: dealParsedManualDiscount.value,
          surcharge: dealParsedSurcharge.value,
          shipping: dealParsedShipping.value,
          calculated_total: dealFinalTotal.value,
        },
      },
    });
    closeDealProductsPopover();
  } catch (error) {
    const message =
      error?.response?.data?.errors?.[0] ||
      t('DEALS.CONVERSATION.PRODUCTS.ERROR_SAVE_DEAL_PRODUCTS');
    useAlert(message);
  } finally {
    dealPopoverSaving.value = false;
  }
};

const toggleLabelOnDeal = async (deal, labelTitle) => {
  labelsSaving.value = true;
  try {
    const currentLabels = [...new Set(getDealLabels(deal))];
    const nextLabels = currentLabels.includes(labelTitle)
      ? currentLabels.filter(item => item !== labelTitle)
      : [...currentLabels, labelTitle];

    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: deal.id,
      deal: { label_list: nextLabels },
    });
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || 'Erro ao atualizar etiquetas'
    );
  } finally {
    labelsSaving.value = false;
  }
};

const openAddLabelModal = () => {
  showAddLabelModal.value = true;
};

const toggleAssigneePopover = (deal, event) => {
  if (activeAssigneePopoverDealId.value === deal.id) {
    activeAssigneePopoverDealId.value = null;
    return;
  }
  activeDealActionsId.value = null;
  activeDealPriorityPopoverId.value = null;
  activeDealLabelsPopoverId.value = null;
  activeDealCustomFieldsPopoverId.value = null;
  activeDealPopoverId.value = null;
  setAssigneePopoverPosition(event);

  const customIds = Array.isArray(
    deal?.custom_attributes?.responsible_agent_ids
  )
    ? deal.custom_attributes.responsible_agent_ids
    : [];
  const nativeId = Number(deal?.assignee?.id || deal?.assignee_id);
  const ids = [
    ...new Set(
      [...customIds, nativeId].filter(id => !Number.isNaN(Number(id)))
    ),
  ];
  assigneeSelection.value = agents.value.filter(agent =>
    ids.includes(Number(agent.id))
  );
  assigneeSearch.value = '';
  activeAssigneePopoverDealId.value = deal.id;
};

const isAssigneeSelected = agentId =>
  assigneeSelection.value.some(item => Number(item.id) === Number(agentId));

const toggleAssigneeSelection = agent => {
  if (isAssigneeSelected(agent.id)) {
    assigneeSelection.value = assigneeSelection.value.filter(
      item => Number(item.id) !== Number(agent.id)
    );
    return;
  }
  assigneeSelection.value = [...assigneeSelection.value, agent];
};

const saveDealAssignees = async deal => {
  assigneeSaving.value = true;
  try {
    const selectedIds = assigneeSelection.value.map(agent => Number(agent.id));
    const primaryAssigneeId = selectedIds[0] || null;
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: deal.id,
      deal: {
        assignee_id: primaryAssigneeId,
        custom_attributes: {
          ...(deal.custom_attributes || {}),
          responsible_agent_ids: selectedIds,
        },
      },
    });
    activeAssigneePopoverDealId.value = null;
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || 'Erro ao atualizar responsaveis'
    );
  } finally {
    assigneeSaving.value = false;
  }
};

const openLoseDealModal = deal => {
  activeDealActionsId.value = null;
  selectedDealForLoss.value = deal;
  selectedLossReasonId.value = null;
  lossJustification.value = '';
  showLoseDealModal.value = true;
};

const closeLoseDealModal = () => {
  showLoseDealModal.value = false;
  selectedDealForLoss.value = null;
  selectedLossReasonId.value = null;
  lossJustification.value = '';
};

const confirmLoseDeal = async () => {
  if (!selectedDealForLoss.value) return;
  if (!selectedLossReasonId.value) {
    useAlert(t('DEALS.CONVERSATION.LOSS_MODAL.REASON_REQUIRED'));
    return;
  }
  if (
    selectedLossReason.value?.requires_justification &&
    !lossJustification.value?.trim()
  ) {
    useAlert(t('DEALS.CONVERSATION.LOSS_MODAL.JUSTIFICATION_REQUIRED_ALERT'));
    return;
  }

  loseDealLoading.value = true;
  try {
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: selectedDealForLoss.value.id,
      deal: {
        status: 'lost',
        deal_loss_reason_id: selectedLossReasonId.value,
        loss_justification: lossJustification.value?.trim() || null,
      },
    });
    closeLoseDealModal();
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] ||
        t('DEALS.CONVERSATION.LOSS_MODAL.ERROR')
    );
  } finally {
    loseDealLoading.value = false;
  }
};

const openConversationDock = async conversationId => {
  if (!conversationId) return;
  const conversationIdNumber = Number(conversationId);

  let conversation = allConversations.value.find(
    item => Number(item.id) === conversationIdNumber
  );

  if (!conversation) {
    await store
      .dispatch('getConversation', conversationIdNumber)
      .catch(() => null);
    const getConversationById = store.getters.getConversationById;
    if (typeof getConversationById === 'function') {
      conversation = getConversationById(conversationIdNumber);
    }
    if (!conversation) {
      conversation = allConversations.value.find(
        item => Number(item.id) === conversationIdNumber
      );
    }
    if (!conversation) {
      conversation = allConversations.value.find(
        item => Number(item.display_id) === conversationIdNumber
      );
    }
  }

  if (!conversation) {
    useAlert('Nao foi possivel carregar a conversa vinculada a este negocio');
    return;
  }

  await store
    .dispatch('setActiveChat', { data: conversation })
    .catch(() => null);
  dockConversationId.value = Number(
    conversation.display_id || conversationIdNumber
  );
  dockConversationInboxId.value = Number(conversation.inbox_id || 0);
  isDockSidebarOpen.value = false;
  showConversationDock.value = true;
};

const closeConversationDock = () => {
  showConversationDock.value = false;
  dockConversationId.value = null;
  dockConversationInboxId.value = 0;
  isDockSidebarOpen.value = false;
  store.dispatch('clearSelectedState');
};

const toggleDockSidebar = () => {
  isDockSidebarOpen.value = !isDockSidebarOpen.value;
};

const handleDockContactClick = event => {
  event.preventDefault();
  event.stopPropagation();
  toggleDockSidebar();
};

const handleDockCloseClick = event => {
  event.preventDefault();
  event.stopPropagation();
  closeConversationDock();
};

const handleDockClickCapture = event => {
  const anchor = event.target?.closest?.('a[href]');
  if (!anchor) return;
  const href = String(anchor.getAttribute('href') || '');
  if (href.includes('/app/accounts/') && href.includes('/contacts/')) {
    event.preventDefault();
    event.stopPropagation();
  }
};

const handleGlobalSubmitCapture = () => {};

const handleBeforeUnload = () => {};

const handlePageHide = () => {};

const saveDockState = () => {
  if (typeof window === 'undefined') return;
  window[DOCK_STATE_KEY] = {
    showConversationDock: showConversationDock.value,
    dockConversationId: dockConversationId.value,
    dockConversationInboxId: dockConversationInboxId.value,
    isDockSidebarOpen: isDockSidebarOpen.value,
    route: route.fullPath,
    at: Date.now(),
  };
};

const restoreDockState = async () => {
  if (typeof window === 'undefined') return;
  const state = window[DOCK_STATE_KEY];
  if (!state?.showConversationDock || !state?.dockConversationId) return;
  const conversationIdNumber = Number(state.dockConversationId);
  let conversation = allConversations.value.find(
    item => Number(item.id) === conversationIdNumber
  );
  if (!conversation) {
    await store
      .dispatch('getConversation', conversationIdNumber)
      .catch(() => null);
    const getConversationById = store.getters.getConversationById;
    if (typeof getConversationById === 'function') {
      conversation = getConversationById(conversationIdNumber);
    }
  }
  if (conversation) {
    await store
      .dispatch('setActiveChat', { data: conversation })
      .catch(() => null);
    dockConversationId.value = conversationIdNumber;
    dockConversationInboxId.value = Number(
      conversation.inbox_id || state.dockConversationInboxId || 0
    );
    isDockSidebarOpen.value = Boolean(state.isDockSidebarOpen);
    showConversationDock.value = true;
  }
};

const closeAddLabelModal = async () => {
  showAddLabelModal.value = false;
  await store.dispatch('labels/get').catch(() => null);
};

const openDealCustomFieldsModal = deal => {
  selectedDealForCustomFields.value = deal;
  selectedCustomAttributeKey.value = null;
  const attrs = deal.custom_attributes || {};
  const definitionKeys = new Set(
    dealAttributeDefinitions.value.map(item => item.attribute_key)
  );
  const keys = Object.keys(attrs).filter(key => {
    if (RESERVED_DEAL_CUSTOM_ATTRIBUTE_KEYS.has(key)) return false;
    return definitionKeys.has(key);
  });
  dealCustomFieldKeys.value = keys;
  dealCustomFieldsDraft.value = keys.reduce((acc, key) => {
    acc[key] = attrs[key];
    return acc;
  }, {});
  showDealCustomFieldsModal.value = true;
  activeDealCustomFieldsPopoverId.value = null;
};

const closeDealCustomFieldsModal = () => {
  showDealCustomFieldsModal.value = false;
  selectedDealForCustomFields.value = null;
  selectedCustomAttributeKey.value = null;
  dealCustomFieldsDraft.value = {};
  dealCustomFieldKeys.value = [];
  activeCustomFieldListKey.value = null;
  customFieldListSearch.value = '';
};

const closeAddDealAttributeModal = () => {
  showAddDealAttributeModal.value = false;
  store.dispatch('attributes/get');
};

const openAddDealAttributeModal = () => {
  showAddDealAttributeModal.value = true;
  activeDealCustomFieldsPopoverId.value = null;
};

const addCustomFieldToDraft = () => {
  if (!selectedCustomAttributeKey.value) return;
  if (dealCustomFieldKeys.value.includes(selectedCustomAttributeKey.value)) {
    selectedCustomAttributeKey.value = null;
    return;
  }
  dealCustomFieldKeys.value.push(selectedCustomAttributeKey.value);
  dealCustomFieldsDraft.value = {
    ...dealCustomFieldsDraft.value,
    [selectedCustomAttributeKey.value]: '',
  };
  selectedCustomAttributeKey.value = null;
};

const removeCustomFieldFromDraft = key => {
  dealCustomFieldKeys.value = dealCustomFieldKeys.value.filter(
    item => item !== key
  );
  const next = { ...dealCustomFieldsDraft.value };
  delete next[key];
  dealCustomFieldsDraft.value = next;
};

const updateDraftCustomFieldValue = (key, value) => {
  dealCustomFieldsDraft.value = {
    ...dealCustomFieldsDraft.value,
    [key]: value,
  };
};

const toggleCustomFieldListPopover = (key, event) => {
  if (activeCustomFieldListKey.value === key) {
    activeCustomFieldListKey.value = null;
    return;
  }
  const trigger = event?.currentTarget;
  const triggerRect = trigger?.getBoundingClientRect?.();
  customFieldListPopoverWidth.value = Math.max(
    220,
    Math.floor(triggerRect?.width || 320)
  );
  customFieldListPopoverStyle.value = setSmallPopoverPosition(event, {
    width: customFieldListPopoverWidth.value,
    preferredHeight: 320,
    minHeight: 220,
    openThreshold: 260,
    align: 'start',
  });
  customFieldListSearch.value = '';
  activeCustomFieldListKey.value = key;
};

const selectCustomFieldListOption = (key, value) => {
  updateDraftCustomFieldValue(key, value);
  activeCustomFieldListKey.value = null;
};

const resetCreateDealForm = () => {
  editingDealId.value = null;
  selectedPipelineId.value = null;
  selectedStageId.value = null;
  newDealTitle.value = '';
  newDealDescription.value = '';
  newDealStartDate.value = '';
  newDealCloseDate.value = '';
  selectedDealContactId.value = contactId.value || null;
  selectedDealConversationId.value = null;
  selectedDealAgentIds.value = [];
  selectedDealLabels.value = [];
  showCreateProductsPopover.value = false;
  createProductsView.value = 'summary';
  createProductSearch.value = '';
  createSelectedProducts.value = [];
  createCouponCode.value = '';
  createSelectedCoupon.value = null;
  createManualDiscount.value = '0';
  createSurcharge.value = '0';
  createShipping.value = '0';
  showCreateProductForm.value = false;
  newProduct.value = { name: '', sku: '', price: '' };
};

const openCreateDealModal = () => {
  resetCreateDealForm();
  selectedDealForForm.value = null;
  showCreateDealModal.value = true;
};

const closeCreateDealModal = () => {
  showCreateDealModal.value = false;
  selectedDealForForm.value = null;
  resetCreateDealForm();
};
watch(selectedPipelineId, () => {
  const firstStage = stageOptions.value[0];
  selectedStageId.value = firstStage ? firstStage.value : null;
});
watch(selectedDealContactId, contactIdValue => {
  if (!contactIdValue) return;
  store.dispatch('contactConversations/get', contactIdValue).catch(() => null);
});

const openEditDealModal = deal => {
  editingDealId.value = deal.id;
  selectedDealForForm.value = deal;
  showCreateProductsPopover.value = false;
  createProductsView.value = 'summary';
  createProductSearch.value = '';
  showCreateProductForm.value = false;
  selectedPipelineId.value = deal.pipeline_id || null;
  selectedStageId.value = deal.pipeline_stage_id || null;
  newDealTitle.value = deal.title || '';
  newDealDescription.value = deal.description || '';
  selectedDealContactId.value = deal.contact_id || contactId.value || null;
  selectedDealConversationId.value = getDealConversationRouteId(deal) || null;
  newDealCloseDate.value = deal.close_date || '';
  createSelectedProducts.value = (deal.products || []).map(item => ({
    product_id: item.product_id,
    name: item.name || item.product?.name || 'Produto',
    sku: item.sku || item.product?.sku || '',
    quantity: Number(item.quantity || 1),
    unit_price: Number(item.unit_price || 0),
  }));
  const attrs = deal.custom_attributes || {};
  newDealStartDate.value = attrs.start_date
    ? String(attrs.start_date).slice(0, 10)
    : '';
  const customAgentIds = Array.isArray(attrs.responsible_agent_ids)
    ? attrs.responsible_agent_ids
        .map(id => Number(id))
        .filter(id => !Number.isNaN(id))
    : [];
  const nativeAssignee = Number(deal.assignee_id || deal.assignee?.id);
  selectedDealAgentIds.value = [
    ...new Set(
      [
        ...customAgentIds,
        Number.isNaN(nativeAssignee) ? null : nativeAssignee,
      ].filter(Boolean)
    ),
  ];
  selectedDealLabels.value = getDealLabels(deal);
  createCouponCode.value = attrs.coupon_code || '';
  createManualDiscount.value = String(attrs.manual_discount || 0);
  createSurcharge.value = String(attrs.surcharge || 0);
  createShipping.value = String(attrs.shipping || 0);
  createSelectedCoupon.value = null;
  if (createCouponCode.value) {
    const normalized = createCouponCode.value.toUpperCase();
    createSelectedCoupon.value = coupons.value.find(
      item => item.active && item.code?.toUpperCase() === normalized
    );
  }
  showCreateDealModal.value = true;
};

const modalPipelineId = computed(() => {
  return (
    Number(selectedDealForForm.value?.pipeline_id) ||
    Number(selectedPipelineId.value) ||
    Number(pipelines.value[0]?.id) ||
    null
  );
});

const modalStages = computed(() => {
  const pipelineId = Number(
    selectedDealForForm.value?.pipeline_id || selectedPipelineId.value
  );
  const stages = pipelineById.value[pipelineId]?.stages || [];
  return [...stages].sort((a, b) => (a.position || 0) - (b.position || 0));
});

const submitDealFromModal = async dealData => {
  if (!dealData) return;
  creatingDeal.value = true;
  try {
    const { selected_products: selectedProductsFromModal = [], ...payload } =
      dealData;

    if (isEditingMode.value) {
      const currentDeal = contactDeals.value.find(
        item => Number(item.id) === Number(editingDealId.value)
      );
      await store.dispatch('deals/update', {
        accountId: accountId.value,
        dealId: editingDealId.value,
        deal: {
          ...payload,
          contact_id: payload.contact_id || contactId.value,
        },
      });

      await syncDealProducts({
        dealId: editingDealId.value,
        existingProducts: currentDeal?.products || [],
        selectedItems: selectedProductsFromModal,
      });
    } else {
      const response = await store.dispatch('deals/create', {
        accountId: accountId.value,
        deal: {
          ...payload,
          contact_id: payload.contact_id || contactId.value,
        },
      });
      const dealResponse = response?.data || response;
      const createdDealId = dealResponse?.id;
      if (createdDealId && selectedProductsFromModal.length > 0) {
        await Promise.all(
          selectedProductsFromModal.map(item =>
            DealProductsAPI.create(accountId.value, createdDealId, {
              product_id: item.product_id,
              quantity: item.quantity,
              unit_price: item.unit_price,
            })
          )
        );
      }
    }

    closeCreateDealModal();
    await store.dispatch('deals/get', {
      accountId: accountId.value,
      params: { contact_id: contactId.value },
    });
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] ||
        t('DEALS.CONVERSATION.DEAL_MODAL.ERROR_CREATE')
    );
  } finally {
    creatingDeal.value = false;
  }
};

const isWonOrLostDeal = deal =>
  ['won', 'lost'].includes(String(deal?.status || '').toLowerCase());

const handleDealAction = async (deal, action) => {
  if (action === 'edit') {
    activeDealActionsId.value = null;
    openEditDealModal(deal);
    return;
  }
  if (action === 'custom_fields') {
    activeDealActionsId.value = null;
    openDealCustomFieldsModal(deal);
    return;
  }
  if (action === 'lost') {
    openLoseDealModal(deal);
    return;
  }
  if (action === 'won') {
    activeDealActionsId.value = null;
    try {
      await store.dispatch('deals/update', {
        accountId: accountId.value,
        dealId: deal.id,
        deal: { status: 'won' },
      });
      return;
    } catch (error) {
      useAlert(
        error?.response?.data?.errors?.[0] ||
          t('DEALS.CONVERSATION.DEAL_MODAL.ERROR_UPDATE')
      );
      return;
    }
  }
  if (action === 'restore') {
    activeDealActionsId.value = null;
    try {
      await store.dispatch('deals/update', {
        accountId: accountId.value,
        dealId: deal.id,
        deal: {
          status: 'open',
          deal_loss_reason_id: null,
          loss_justification: null,
        },
      });
      return;
    } catch (error) {
      useAlert(
        error?.response?.data?.errors?.[0] ||
          t('DEALS.CONVERSATION.DEAL_MODAL.ERROR_UPDATE')
      );
      return;
    }
  }
  if (action === 'delete') {
    activeDealActionsId.value = null;
    const confirmed = await confirmDealDeleteModal.value.showConfirmation();
    if (!confirmed) return;
    try {
      await store.dispatch('deals/delete', {
        accountId: accountId.value,
        dealId: deal.id,
      });
      return;
    } catch (error) {
      useAlert(error?.response?.data?.errors?.[0] || 'Erro ao excluir negocio');
      return;
    }
  }
  activeDealActionsId.value = null;
  useAlert(t('DEALS.CONVERSATION.ACTIONS.COMING_SOON'));
};

const saveDealCustomFields = async () => {
  if (!selectedDealForCustomFields.value) return;
  customFieldsSaving.value = true;
  try {
    const currentAttrs =
      selectedDealForCustomFields.value.custom_attributes || {};
    const reservedAttrs = Object.entries(currentAttrs).reduce(
      (acc, [key, value]) => {
        if (RESERVED_DEAL_CUSTOM_ATTRIBUTE_KEYS.has(key)) acc[key] = value;
        return acc;
      },
      {}
    );

    const nextCustomFields = dealCustomFieldKeys.value.reduce((acc, key) => {
      const value = dealCustomFieldsDraft.value[key];
      if (isCustomFieldValuePresent(value)) {
        acc[key] = value;
      }
      return acc;
    }, {});

    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: selectedDealForCustomFields.value.id,
      deal: {
        custom_attributes: {
          ...reservedAttrs,
          ...nextCustomFields,
        },
      },
    });
    closeDealCustomFieldsModal();
  } catch (error) {
    useAlert(error?.response?.data?.errors?.[0] || 'Erro ao salvar atributos');
  } finally {
    customFieldsSaving.value = false;
  }
};

const handleDocumentClick = event => {
  const target = event.target;
  if (
    activeDealActionsId.value &&
    !target.closest('[data-contact-deal-actions-menu]') &&
    !target.closest('[data-contact-deal-actions-trigger]')
  ) {
    activeDealActionsId.value = null;
  }
  if (
    activeDealPriorityPopoverId.value &&
    !target.closest('[data-contact-deal-priority-menu]') &&
    !target.closest('[data-contact-deal-priority-trigger]')
  ) {
    activeDealPriorityPopoverId.value = null;
  }
  if (
    activeDealLabelsPopoverId.value &&
    !target.closest('[data-contact-deal-labels-popover]') &&
    !target.closest('[data-contact-deal-labels-trigger]')
  ) {
    activeDealLabelsPopoverId.value = null;
  }
  if (
    activeDealPopoverId.value &&
    !target.closest('[data-contact-deal-products-popover]') &&
    !target.closest('[data-contact-deal-products-trigger]')
  ) {
    closeDealProductsPopover();
  }
  if (
    activeDealCustomFieldsPopoverId.value &&
    !target.closest('[data-contact-deal-custom-fields-popover]') &&
    !target.closest('[data-contact-deal-custom-fields-trigger]')
  ) {
    activeDealCustomFieldsPopoverId.value = null;
  }
  if (
    activeAssigneePopoverDealId.value &&
    !target.closest('[data-contact-deal-assignee-popover]') &&
    !target.closest('[data-contact-deal-assignee-trigger]')
  ) {
    activeAssigneePopoverDealId.value = null;
  }
  if (
    activeCustomFieldListKey.value &&
    !target.closest('[data-custom-field-list-popover]') &&
    !target.closest('[data-custom-field-list-trigger]')
  ) {
    activeCustomFieldListKey.value = null;
  }
  if (
    showCreateProductsPopover.value &&
    !target.closest('[data-create-products-popover]') &&
    !target.closest('[data-create-products-trigger]')
  ) {
    showCreateProductsPopover.value = false;
  }
};

const setDealStage = async (deal, stageId) => {
  if (!deal?.id) return;
  if (Number(deal.pipeline_stage_id) === Number(stageId)) return;

  stageUpdatingDealId.value = deal.id;
  try {
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: deal.id,
      deal: { pipeline_stage_id: stageId },
    });
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] ||
        t('DEALS.CONVERSATION.DEAL_MODAL.ERROR_UPDATE')
    );
  } finally {
    stageUpdatingDealId.value = null;
  }
};

const loadDeals = async () => {
  if (!accountId.value || !contactId.value) return;
  await Promise.allSettled([
    store.dispatch('pipelines/get', { accountId: accountId.value }),
    store.dispatch('agents/get'),
    fetchContactsListWithCache(store, { accountId: accountId.value }),
    store
      .dispatch('dealLossReasons/get', { accountId: accountId.value })
      .catch(() => null),
    store.dispatch('labels/get').catch(() => null),
    store.dispatch('products/get', { accountId: accountId.value }),
    store.dispatch('coupons/get', { accountId: accountId.value }),
    store.dispatch('attributes/get'),
    store.dispatch('deals/get', {
      accountId: accountId.value,
      params: { contact_id: contactId.value },
    }),
  ]);
  initialLoadDone.value = true;
};

onMounted(loadDeals);
watch(contactId, loadDeals);
watch(isDockSidebarOpen, () => saveDockState());
watch(dockConversationId, () => saveDockState());
watch(dockConversationInboxId, () => saveDockState());
onMounted(() => {
  document.addEventListener('mousedown', handleDocumentClick);
  document.addEventListener('submit', handleGlobalSubmitCapture, true);
  window.addEventListener('beforeunload', handleBeforeUnload);
  window.addEventListener('pagehide', handlePageHide);
  restoreDockState();
});
onBeforeUnmount(() => {
  saveDockState();
  document.removeEventListener('mousedown', handleDocumentClick);
  document.removeEventListener('submit', handleGlobalSubmitCapture, true);
  window.removeEventListener('beforeunload', handleBeforeUnload);
  window.removeEventListener('pagehide', handlePageHide);
});
</script>

<template>
  <div
    v-if="isLoading"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div v-else class="px-4 py-4 space-y-3">
    <div class="flex items-start justify-between gap-3">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('CONTACT_DEALS.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('CONTACT_DEALS.SUBTITLE') }}
        </p>
      </div>
      <button
        type="button"
        class="inline-flex items-center gap-1 text-sm text-n-brand hover:underline"
        @click.stop.prevent="openCreateDealModal"
      >
        <span class="i-lucide-plus size-4" />
        {{ t('CONTACT_DEALS.CREATE') }}
      </button>
    </div>

    <div
      v-if="!contactDeals.length"
      class="rounded-xl border border-n-weak bg-n-solid-1 px-4 py-6 text-sm text-center text-n-slate-11"
    >
      {{ t('CONTACT_DEALS.EMPTY') }}
    </div>

    <div
      v-for="deal in contactDeals"
      :key="deal.id"
      class="rounded-xl border border-n-weak bg-n-solid-1 px-3 py-3 relative"
    >
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0 flex-1 space-y-1.5">
          <p class="truncate text-sm font-semibold text-n-slate-12">
            <span class="inline-flex items-center gap-1.5">
              <span
                v-if="deal.status === 'won'"
                class="i-lucide-circle-check size-4 text-green-600"
                :title="t('DEALS.STATUS.WON')"
              />
              <span
                v-else-if="deal.status === 'lost'"
                class="i-lucide-circle-x size-4 text-red-600"
                :title="t('DEALS.STATUS.LOST')"
              />
              <span>{{ getDealTitle(deal) }}</span>
            </span>
          </p>
          <button
            type="button"
            data-contact-deal-products-trigger
            class="text-xs text-n-brand mt-1 hover:underline inline-flex items-center gap-1"
            :title="`${t('CONTACT_DEALS.PRODUCTS_TITLE_PREFIX')}: ${getProductSummary(deal)}`"
            @click.stop.prevent="openDealProductsPopover(deal, $event)"
          >
            <span class="i-lucide-package size-3.5" />
            {{ getProductSummary(deal) }}
          </button>
          <button
            v-if="getDealConversationRouteId(deal)"
            type="button"
            class="text-xs text-n-brand mt-1 hover:underline inline-flex items-center gap-1 ml-2"
            :title="`${t('CONTACT_DEALS.OPEN_CONVERSATION_PREFIX')} #${getDealConversationRouteId(deal)}`"
            @click.stop.prevent="
              openConversationDock(getDealConversationRouteId(deal))
            "
          >
            <span class="i-lucide-messages-square size-3.5" />
            {{ t('CONTACT_DEALS.CONVERSATION_LABEL') }}
          </button>
          <div class="flex items-center gap-2 text-xs text-n-slate-11">
            <span class="i-lucide-circle-dollar-sign size-4 text-n-slate-10" />
            <span>{{
              formatCurrency(getDealDisplayAmount(deal), deal.currency)
            }}</span>
          </div>
        </div>

        <div class="shrink-0 flex items-start gap-3">
          <div class="text-center">
            <p class="text-sm font-semibold text-n-slate-12">
              {{ getDaysSince(deal.created_at) }}
            </p>
            <p class="text-[11px] text-n-slate-11">
              {{ t('CONTACT_DEALS.OPEN_LABEL') }}
            </p>
          </div>

          <div class="text-center">
            <p class="text-sm font-semibold text-n-slate-12">
              {{ getDaysSince(deal.updated_at || deal.created_at) }}
            </p>
            <p class="text-[11px] text-n-slate-11">
              {{ t('CONTACT_DEALS.STAGE_LABEL') }}
            </p>
          </div>

          <button
            type="button"
            class="p-1.5 rounded hover:bg-n-alpha-2"
            data-contact-deal-actions-trigger
            :title="t('CONTACT_DEALS.MORE_OPTIONS')"
            @click.stop="toggleDealActions(deal.id)"
          >
            <span class="i-lucide-ellipsis size-4 text-n-slate-11" />
          </button>
        </div>
      </div>

      <div
        v-if="activeDealActionsId === deal.id"
        data-contact-deal-actions-menu
        class="absolute z-30 top-10 right-2 w-56 rounded-lg border border-n-weak bg-n-solid-1 shadow-lg py-1"
      >
        <button
          type="button"
          class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
          @click.stop="handleDealAction(deal, 'activity')"
        >
          <span class="i-lucide-calendar-plus size-4 text-n-slate-11" />{{
            t('DEALS.CONVERSATION.ACTIONS.CREATE_ACTIVITY')
          }}
        </button>
        <button
          type="button"
          class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
          @click.stop="handleDealAction(deal, 'edit')"
        >
          <span class="i-lucide-pencil size-4 text-n-slate-11" />{{
            t('DEALS.CONVERSATION.ACTIONS.EDIT_DEAL')
          }}
        </button>
        <button
          type="button"
          class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
          @click.stop="handleDealAction(deal, 'custom_fields')"
        >
          <span class="i-lucide-code size-4 text-n-slate-11" />{{
            t('DEALS.CONVERSATION.ACTIONS.ADD_CUSTOM_ATTRIBUTES')
          }}
        </button>
        <button
          v-if="isWonOrLostDeal(deal)"
          type="button"
          class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
          @click.stop="handleDealAction(deal, 'restore')"
        >
          <span class="i-lucide-rotate-ccw size-4 text-n-slate-11" />{{
            t('CONTACT_DEALS.RESTORE_STATUS')
          }}
        </button>
        <template v-else>
          <button
            type="button"
            class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
            @click.stop="handleDealAction(deal, 'won')"
          >
            <span class="i-lucide-check size-4 text-n-slate-11" />{{
              t('DEALS.CONVERSATION.ACTIONS.WIN_DEAL')
            }}
          </button>
          <button
            type="button"
            class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
            @click.stop="handleDealAction(deal, 'lost')"
          >
            <span class="i-lucide-x size-4 text-n-slate-11" />{{
              t('DEALS.CONVERSATION.ACTIONS.LOSE_DEAL')
            }}
          </button>
        </template>
        <button
          type="button"
          class="w-full text-left px-3 py-2 text-sm text-ruby-10 hover:bg-ruby-2 inline-flex items-center gap-2"
          @click.stop="handleDealAction(deal, 'delete')"
        >
          <span class="i-lucide-trash-2 size-4" />{{
            t('DEALS.CONVERSATION.ACTIONS.DELETE_DEAL')
          }}
        </button>
      </div>

      <div class="mt-2 space-y-1.5 text-xs text-n-slate-11">
        <div
          class="flex items-center gap-2"
          :title="`${t('CONTACT_DEALS.CLOSE_DATE_PREFIX')}: ${formatDate(deal.close_date)}`"
        >
          <span class="i-lucide-calendar size-4 text-n-slate-10" />
          <span>{{ formatDate(deal.close_date) }}</span>
        </div>
        <div
          class="flex items-center gap-2"
          :title="t('DEALS.CONVERSATION.NO_ACTIVITIES')"
        >
          <span class="i-lucide-calendar-plus size-4 text-n-slate-10" />
          <span>{{ t('DEALS.CONVERSATION.NO_ACTIVITIES') }}</span>
        </div>
        <div class="flex items-center gap-2 text-xs text-n-slate-11">
          <span class="i-lucide-funnel size-4 text-n-slate-10" />
          <span class="truncate">{{ getPipelineName(deal) }}</span>
        </div>
        <div class="flex gap-1 mt-1">
          <button
            v-for="(stage, index) in getStageList(deal)"
            :key="`stage-progress-${deal.id}-${stage.id}`"
            type="button"
            class="h-2 flex-1 rounded-full transition-opacity disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="stageUpdatingDealId === deal.id"
            :title="`${t('CONTACT_DEALS.STAGE_TITLE_PREFIX')}: ${getStageTooltip(stage, deal)}`"
            :style="getStageProgressStyle(stage, index, deal)"
            @click="setDealStage(deal, stage.id)"
          />
        </div>
      </div>

      <div class="mt-2 flex items-center justify-between">
        <div class="flex items-center gap-2 min-h-9">
          <button
            type="button"
            data-contact-deal-assignee-trigger
            class="inline-flex h-9 min-w-9 px-1 items-center justify-center rounded-md hover:bg-n-alpha-2"
            :title="t('CONTACT_DEALS.ASSIGNEES_TITLE')"
            @click.stop.prevent="toggleAssigneePopover(deal, $event)"
          >
            <div v-if="getDealAssignees(deal).length" class="flex items-center">
              <span
                v-for="(agent, index) in getDealAssignees(deal).slice(0, 3)"
                :key="`deal-agent-${deal.id}-${agent.id}`"
                class="inline-flex"
                :class="index > 0 ? '-ml-2' : ''"
              >
                <Avatar
                  :name="agent.name"
                  :src="agent.thumbnail || agent.avatar_url"
                  :size="24"
                  rounded-full
                />
              </span>
              <span
                v-if="getDealAssignees(deal).length > 3"
                class="-ml-2 inline-flex h-6 min-w-6 px-1 items-center justify-center rounded-full bg-n-alpha-2 text-[10px] text-n-slate-12"
              >
                +{{ getDealAssignees(deal).length - 3 }}
              </span>
            </div>
            <span
              v-else
              class="inline-flex items-center justify-center size-6 rounded-full bg-n-alpha-2 text-n-slate-11"
              :title="t('CONTACT_DEALS.NO_ASSIGNEE')"
            >
              <span class="i-lucide-user size-4" />
            </span>
          </button>
          <button
            type="button"
            data-contact-deal-custom-fields-trigger
            class="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
            :title="`${t('CONTACT_DEALS.CUSTOM_FIELDS_PREFIX')}: ${getDealCustomFieldsCount(deal)}`"
            @click.stop.prevent="toggleDealCustomFieldsPopover(deal, $event)"
          >
            <span class="i-lucide-code size-6" />
            <span
              class="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-n-brand text-white text-[10px] leading-4 text-center"
            >
              {{ getDealCustomFieldsCount(deal) }}
            </span>
          </button>
          <div class="relative">
            <button
              type="button"
              data-contact-deal-priority-trigger
              class="inline-flex h-9 w-9 items-center justify-center rounded-md text-n-slate-12 hover:bg-n-alpha-2"
              :title="`${t('CONTACT_DEALS.PRIORITY_PREFIX')}: ${getDealPriorityMeta(deal).label}`"
              @click.stop.prevent="toggleDealPriorityPopover(deal.id, $event)"
            >
              <span
                class="size-6"
                :class="[getDealPriorityMeta(deal).icon]"
                :style="{ color: getDealPriorityMeta(deal).color }"
              />
            </button>
          </div>
        </div>
        <span
          class="inline-flex h-9 items-center gap-1 text-xs text-n-slate-11"
          :title="`${t('CONTACT_DEALS.CREATED_AT_PREFIX')}: ${formatDate(deal.created_at)}`"
        >
          <span class="i-lucide-clock-3 size-4" />
          {{ formatDealCreatedAt(deal.created_at) }}
        </span>
      </div>

      <div
        class="mt-2 border-t border-n-weak/80 pt-2 flex items-start justify-between gap-2"
      >
        <div
          class="min-w-0 flex-1 flex flex-wrap gap-1.5 max-h-12 overflow-hidden"
        >
          <span
            v-for="labelTitle in getVisibleDealLabels(deal)"
            :key="`contact-label-chip-${deal.id}-${labelTitle}`"
            class="inline-flex items-center gap-1.5 rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-12"
          >
            <span
              class="inline-block h-2 w-2 shrink-0 rounded-sm"
              :style="{ backgroundColor: getLabelColorByTitle(labelTitle) }"
            />
            <span class="truncate max-w-[120px]">{{ labelTitle }}</span>
          </span>
          <span
            v-if="!getVisibleDealLabels(deal).length"
            class="text-xs text-n-slate-11 py-1"
          >
            {{ t('CONTACT_DEALS.NO_LABELS') }}
          </span>
        </div>

        <button
          type="button"
          data-contact-deal-labels-trigger
          class="relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-n-weak text-n-slate-12"
          :title="`${t('CONTACT_DEALS.LABELS_PREFIX')}: ${getDealLabels(deal).length}`"
          @click.stop.prevent="toggleDealLabelsPopover(deal.id)"
        >
          <span class="i-lucide-tags size-5" />
          <span
            v-if="getHiddenDealLabelsCount(deal) > 0"
            class="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-n-brand text-white text-[10px] leading-4 text-center"
          >
            +{{ getHiddenDealLabelsCount(deal) }}
          </span>
        </button>
      </div>

      <div class="relative">
        <div
          v-if="activeDealLabelsPopoverId === deal.id"
          data-contact-deal-labels-popover
          class="absolute z-30 right-0 bottom-10 w-[300px] max-w-[calc(100vw-2rem)] overflow-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-xl p-3 space-y-3"
        >
          <input
            v-model="dealLabelsSearch"
            type="text"
            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
            :placeholder="t('DEALS.CONVERSATION.PRODUCTS.SEARCH_PLACEHOLDER')"
          />
          <div
            class="max-h-52 overflow-y-auto border border-n-weak rounded-lg divide-y divide-n-weak"
          >
            <button
              v-for="label in filteredLabels"
              :key="`contact-deal-label-${deal.id}-${label.id}`"
              type="button"
              class="w-full px-3 py-2 text-sm hover:bg-n-alpha-2 flex items-center justify-between gap-2 text-n-slate-12"
              :disabled="labelsSaving"
              @click="toggleLabelOnDeal(deal, label.title)"
            >
              <span class="inline-flex items-center gap-2 min-w-0">
                <span
                  class="inline-flex size-2.5 rounded-full shrink-0"
                  :style="{ backgroundColor: label.color || '#3B82F6' }"
                />
                <span class="truncate">{{ label.title }}</span>
              </span>
              <span
                v-if="getDealLabels(deal).includes(label.title)"
                class="i-lucide-check size-4 text-green-600 shrink-0"
              />
            </button>
            <p
              v-if="!filteredLabels.length"
              class="px-3 py-2 text-sm text-n-slate-11"
            >
              {{ t('DEALS.CONVERSATION.PRODUCTS.EMPTY_FOUND') }}
            </p>
          </div>
          <button
            type="button"
            class="text-sm text-n-brand hover:underline"
            @click="openAddLabelModal"
          >
            {{ t('CONTACT_DEALS.CREATE_LABEL') }}
          </button>
        </div>
      </div>

      <teleport to="body">
        <div
          v-if="activeDealPriorityPopoverId === deal.id"
          data-contact-deal-priority-menu
          class="fixed z-[12000] w-[230px] max-w-[calc(100vw-2rem)] overflow-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-xl p-2 space-y-1"
          :style="dealPriorityPopoverStyle"
        >
          <button
            v-for="option in PRIORITY_OPTIONS"
            :key="`contact-priority-${option.value}`"
            type="button"
            class="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2 text-n-slate-12"
            :disabled="prioritySaving"
            @click="setDealPriority(deal, option.value)"
          >
            <span
              class="size-4"
              :class="[option.icon]"
              :style="{ color: option.color }"
            />
            <span>{{ option.label }}</span>
          </button>
        </div>
      </teleport>

      <teleport to="body">
        <div
          v-if="activeAssigneePopoverDealId === deal.id"
          data-contact-deal-assignee-popover
          class="fixed z-[12000] w-[320px] max-w-[calc(100vw-2rem)] overflow-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-xl p-3 space-y-3"
          :style="assigneePopoverStyle"
        >
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACT_DEALS.ASSIGNEES_TITLE') }}
          </p>
          <div class="space-y-2">
            <input
              v-model="assigneeSearch"
              type="text"
              class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-solid-1 text-sm"
              :placeholder="t('CONTACT_DEALS.SEARCH_AGENT_PLACEHOLDER')"
            />
            <div
              class="max-h-52 overflow-y-auto border border-n-weak rounded-md py-1"
            >
              <button
                v-for="agent in filteredAssigneeOptions"
                :key="`assignee-option-${deal.id}-${agent.id}`"
                type="button"
                class="w-full px-3 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2 inline-flex items-center justify-between gap-2"
                @click="toggleAssigneeSelection(agent)"
              >
                <span class="inline-flex items-center gap-2 min-w-0">
                  <Avatar
                    :name="agent.name"
                    :src="agent.thumbnail || agent.avatar_url"
                    :size="20"
                    rounded-full
                  />
                  <span class="truncate">{{ agent.name }}</span>
                </span>
                <span
                  v-if="isAssigneeSelected(agent.id)"
                  class="i-lucide-check size-4 text-n-brand shrink-0"
                />
              </button>
              <p
                v-if="!filteredAssigneeOptions.length"
                class="px-3 py-2 text-sm text-n-slate-11"
              >
                {{ t('CONTACT_DEALS.NO_AGENTS_FOUND') }}
              </p>
            </div>
          </div>
          <div class="flex justify-end gap-2">
            <Button
              slate
              faded
              xs
              :label="t('DEALS.CONVERSATION.PRODUCTS.CANCEL')"
              @click="activeAssigneePopoverDealId = null"
            />
            <Button
              xs
              :is-loading="assigneeSaving"
              :label="t('DEALS.CONVERSATION.PRODUCTS.SAVE')"
              @click="saveDealAssignees(deal)"
            />
          </div>
        </div>
      </teleport>

      <teleport to="body">
        <div
          v-if="activeDealCustomFieldsPopoverId === deal.id"
          data-contact-deal-custom-fields-popover
          class="fixed z-[12000] w-[360px] max-w-[calc(100vw-2rem)] overflow-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-xl p-3 space-y-3"
          :style="dealCustomFieldsPopoverStyle"
        >
          <div class="flex items-center justify-between gap-2">
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.TITLE') }}
            </p>
            <button
              type="button"
              class="text-xs text-n-slate-11 hover:underline"
              @click="closeDealCustomFieldsPopover"
            >
              {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.CLOSE') }}
            </button>
          </div>

          <input
            v-model="dealCustomFieldsSearch"
            type="text"
            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-2 text-sm"
            :placeholder="
              t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.SEARCH_PLACEHOLDER')
            "
          />

          <div
            class="max-h-60 overflow-y-auto border border-n-weak rounded-lg divide-y divide-n-weak"
          >
            <div
              v-for="item in getDealCustomFieldsForPopover(deal)"
              :key="`${deal.id}-${item.key}`"
              class="px-3 py-2"
            >
              <p class="text-xs text-n-slate-10">{{ item.label }}</p>
              <p class="text-sm text-n-slate-12 break-words">
                {{ item.formattedValue }}
              </p>
            </div>
            <p
              v-if="!getDealCustomFieldsForPopover(deal).length"
              class="px-3 py-4 text-sm text-center text-n-slate-11"
            >
              {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.EMPTY_FOUND') }}
            </p>
          </div>

          <div class="flex items-center justify-between">
            <button
              type="button"
              class="text-sm text-n-brand"
              @click="openDealCustomFieldsModal(deal)"
            >
              {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.MANAGE') }}
            </button>
            <button
              type="button"
              class="text-sm text-n-brand"
              @click="openAddDealAttributeModal"
            >
              {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.CREATE') }}
            </button>
          </div>
        </div>
      </teleport>

      <div class="relative">
        <div
          v-if="activeDealPopoverId === deal.id"
          data-contact-deal-products-popover
          class="fixed z-[5000] w-[380px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-1.5rem)] flex flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-xl"
          :style="dealPopoverStyle"
        >
          <template v-if="dealProductsView === 'summary'">
            <div class="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
              <div
                v-if="!dealSelectedProducts.length"
                class="text-center py-6 text-sm text-n-slate-12"
              >
                {{ t('DEALS.CONVERSATION.PRODUCTS.EMPTY') }}
              </div>

              <div v-else class="space-y-2">
                <div
                  v-for="item in dealSelectedProducts"
                  :key="item.product_id"
                  class="border-b border-n-weak pb-2 last:border-0 last:pb-0"
                >
                  <div class="flex items-center justify-between gap-2">
                    <p class="text-sm text-n-slate-12 truncate">
                      {{ item.name }}
                    </p>
                    <span class="text-sm font-medium text-n-slate-12">{{
                      formatCurrency(item.unit_price)
                    }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-2 mt-1">
                    <p class="text-xs text-n-slate-11 truncate">
                      {{ item.sku || '-' }}
                    </p>
                    <div class="flex items-center border border-n-weak rounded">
                      <button
                        type="button"
                        class="px-2 py-0.5 text-n-slate-12"
                        @click="decreaseDealQty(item)"
                      >
                        -
                      </button>
                      <span class="px-2 text-sm text-n-slate-12">{{
                        item.quantity
                      }}</span>
                      <button
                        type="button"
                        class="px-2 py-0.5 text-n-slate-12"
                        @click="increaseDealQty(item)"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="text-right">
                <button
                  type="button"
                  class="text-sm text-n-brand"
                  @click="dealProductsView = 'catalog'"
                >
                  {{ t('DEALS.CONVERSATION.PRODUCTS.ADD_PRODUCT') }}
                </button>
              </div>

              <div
                class="border-t border-n-weak pt-2 space-y-1.5 text-sm text-n-slate-12"
              >
                <div class="flex justify-between">
                  <span>{{
                    t('DEALS.CONVERSATION.PRODUCTS.TOTAL_PRODUCTS')
                  }}</span
                  ><span class="font-medium">{{
                    formatCurrency(dealSubtotalProducts)
                  }}</span>
                </div>
                <div class="flex items-center justify-between gap-2">
                  <span>{{ t('DEALS.CONVERSATION.PRODUCTS.COUPON') }}</span>
                  <input
                    v-model="dealCouponCode"
                    type="text"
                    class="w-40 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                    :placeholder="
                      t('DEALS.CONVERSATION.PRODUCTS.COUPON_PLACEHOLDER')
                    "
                    @blur="applyDealCoupon"
                  />
                </div>
                <div class="flex items-center justify-between gap-2">
                  <span>{{ t('CONTACT_DEALS.DISCOUNT_LABEL') }}</span>
                  <input
                    v-model="dealManualDiscount"
                    type="number"
                    min="0"
                    step="0.01"
                    class="w-28 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                  />
                </div>
                <div class="flex items-center justify-between gap-2">
                  <span>{{ t('CONTACT_DEALS.SURCHARGE_LABEL') }}</span>
                  <input
                    v-model="dealSurcharge"
                    type="number"
                    min="0"
                    step="0.01"
                    class="w-28 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                  />
                </div>
                <div class="flex items-center justify-between gap-2">
                  <span>{{ t('CONTACT_DEALS.SHIPPING_LABEL') }}</span>
                  <input
                    v-model="dealShipping"
                    type="number"
                    min="0"
                    step="0.01"
                    class="w-28 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                  />
                </div>
              </div>
            </div>

            <div
              class="shrink-0 border-t border-n-weak bg-n-solid-1 p-3 space-y-2"
            >
              <div
                class="flex items-center justify-between text-base font-semibold text-n-slate-12"
              >
                <span>{{ t('DEALS.CONVERSATION.PRODUCTS.TOTAL') }}</span>
                <span>{{ formatCurrency(dealFinalTotal) }}</span>
              </div>

              <div class="flex justify-end gap-2">
                <Button
                  slate
                  faded
                  xs
                  :label="t('DEALS.CONVERSATION.PRODUCTS.CANCEL')"
                  @click="closeDealProductsPopover"
                />
                <Button
                  xs
                  :is-loading="dealPopoverSaving"
                  :label="t('DEALS.CONVERSATION.PRODUCTS.SAVE')"
                  @click="saveDealProducts(deal)"
                />
              </div>
            </div>
          </template>

          <template v-else>
            <div class="flex-1 overflow-y-auto p-3 space-y-3">
              <input
                v-model="dealProductSearch"
                type="text"
                class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
                :placeholder="
                  t('DEALS.CONVERSATION.PRODUCTS.SEARCH_PLACEHOLDER')
                "
              />

              <div
                class="max-h-56 overflow-y-auto border border-n-weak rounded-lg py-1"
              >
                <button
                  v-for="product in filteredDealCatalogProducts"
                  :key="product.id"
                  type="button"
                  class="w-full text-left px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2"
                  @click="addProductToDealPopover(product)"
                >
                  {{ product.name }}
                </button>
                <p
                  v-if="!filteredDealCatalogProducts.length"
                  class="px-3 py-2 text-sm text-n-slate-12"
                >
                  {{ t('DEALS.CONVERSATION.PRODUCTS.EMPTY_FOUND') }}
                </p>
              </div>

              <div class="flex items-center justify-between">
                <button
                  type="button"
                  class="text-sm text-n-slate-12"
                  @click="dealProductsView = 'summary'"
                >
                  {{ t('DEALS.CONVERSATION.PRODUCTS.BACK') }}
                </button>
                <button
                  type="button"
                  class="text-sm text-n-brand"
                  @click="
                    showCreateDealProductForm = !showCreateDealProductForm
                  "
                >
                  {{ t('DEALS.CONVERSATION.PRODUCTS.CREATE') }}
                </button>
              </div>

              <div
                v-if="showCreateDealProductForm"
                class="space-y-2 border border-n-weak rounded-lg p-2"
              >
                <input
                  v-model="dealNewProduct.name"
                  type="text"
                  class="w-full px-2 py-1.5 border rounded border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
                  :placeholder="
                    t(
                      'DEALS.CONVERSATION.PRODUCTS.NEW_PRODUCT_NAME_PLACEHOLDER'
                    )
                  "
                />
                <input
                  v-model="dealNewProduct.sku"
                  type="text"
                  class="w-full px-2 py-1.5 border rounded border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
                  :placeholder="
                    t('DEALS.CONVERSATION.PRODUCTS.NEW_PRODUCT_SKU_PLACEHOLDER')
                  "
                />
                <input
                  v-model="dealNewProduct.price"
                  type="number"
                  min="0"
                  step="0.01"
                  class="w-full px-2 py-1.5 border rounded border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
                  :placeholder="
                    t(
                      'DEALS.CONVERSATION.PRODUCTS.NEW_PRODUCT_PRICE_PLACEHOLDER'
                    )
                  "
                />
                <div class="flex justify-end gap-2">
                  <Button
                    slate
                    faded
                    xs
                    :label="t('DEALS.CONVERSATION.PRODUCTS.CANCEL')"
                    @click="showCreateDealProductForm = false"
                  />
                  <Button
                    xs
                    :label="t('DEALS.CONVERSATION.PRODUCTS.SAVE_PRODUCT')"
                    @click="createDealProductQuickly"
                  />
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>

  <woot-modal
    v-model:show="showDealCustomFieldsModal"
    :on-close="closeDealCustomFieldsModal"
  >
    <div class="flex min-h-0 max-h-[82vh] flex-col">
      <woot-modal-header
        :header-title="t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.MODAL_TITLE')"
        :header-content="t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.MODAL_DESC')"
      />
      <div class="modal-content w-full !pt-8 flex min-h-0 flex-col gap-4">
        <div class="space-y-2">
          <label class="text-sm font-medium text-n-slate-12 block">
            {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.FIELD_LABEL') }}
          </label>
          <ComboBox
            v-model="selectedCustomAttributeKey"
            :options="dealAttributeDefinitionOptions"
            :placeholder="
              t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.FIELD_PLACEHOLDER')
            "
            class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
          />
          <div class="flex justify-end">
            <Button
              xs
              :label="t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.ADD_BUTTON')"
              @click="addCustomFieldToDraft"
            />
          </div>
        </div>

        <div class="flex-1 min-h-0 space-y-3 overflow-y-auto pr-1">
          <div
            v-for="key in dealCustomFieldKeys"
            :key="key"
            class="rounded-lg border border-n-weak p-3 space-y-2.5"
          >
            <div class="flex items-center justify-between gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                {{ getDraftCustomFieldLabel(key) }}
              </label>
              <button
                type="button"
                class="text-xs text-ruby-10 hover:underline"
                @click="removeCustomFieldFromDraft(key)"
              >
                {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.REMOVE') }}
              </button>
            </div>

            <input
              v-if="
                ['text', 'link', 'phone_number'].includes(
                  getDraftCustomFieldType(key)
                )
              "
              :value="dealCustomFieldsDraft[key] || ''"
              type="text"
              class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
              @input="updateDraftCustomFieldValue(key, $event.target.value)"
            />

            <input
              v-else-if="
                ['number', 'currency'].includes(getDraftCustomFieldType(key))
              "
              :value="dealCustomFieldsDraft[key] || ''"
              type="number"
              class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
              @input="
                updateDraftCustomFieldValue(
                  key,
                  $event.target.value === '' ? '' : Number($event.target.value)
                )
              "
            />

            <input
              v-else-if="getDraftCustomFieldType(key) === 'date'"
              :value="dealCustomFieldsDraft[key] || ''"
              type="date"
              class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
              @input="updateDraftCustomFieldValue(key, $event.target.value)"
            />

            <button
              v-else-if="getDraftCustomFieldType(key) === 'list'"
              type="button"
              data-custom-field-list-trigger
              class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-solid-1 text-sm text-left text-n-slate-12 inline-flex items-center justify-between gap-2 hover:border-n-slate-6"
              @click="toggleCustomFieldListPopover(key, $event)"
            >
              <span
                class="truncate"
                :class="{
                  'text-n-slate-11': !(dealCustomFieldsDraft[key] || ''),
                }"
              >
                {{ getDraftCustomFieldSelectedLabel(key) }}
              </span>
              <span
                class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-11"
              />
            </button>

            <teleport to="body">
              <div
                v-if="activeCustomFieldListKey === key"
                data-custom-field-list-popover
                class="fixed z-[13000] max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-n-strong bg-n-solid-1 shadow-lg"
                :style="{
                  ...customFieldListPopoverStyle,
                  width: `${customFieldListPopoverWidth}px`,
                }"
              >
                <div class="relative border-b border-n-strong">
                  <span
                    class="absolute i-lucide-search top-2.5 size-4 left-3 text-n-slate-11"
                  />
                  <input
                    v-model="customFieldListSearch"
                    type="text"
                    class="reset-base w-full py-2 pl-10 pr-2 text-sm focus:outline-none border-none rounded-t-md bg-n-solid-1 text-n-slate-12"
                    :placeholder="
                      t(
                        'DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.SEARCH_PLACEHOLDER'
                      )
                    "
                  />
                </div>
                <div class="max-h-60 overflow-y-auto py-1">
                  <button
                    v-for="option in getFilteredDraftCustomFieldOptions(key)"
                    :key="`opt-${key}-${option}`"
                    type="button"
                    class="w-full px-3 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2 inline-flex items-center justify-between gap-2"
                    :class="{
                      'bg-n-alpha-2 font-medium':
                        dealCustomFieldsDraft[key] === option,
                    }"
                    @click="selectCustomFieldListOption(key, option)"
                  >
                    <span class="truncate">{{ option }}</span>
                    <span
                      v-if="dealCustomFieldsDraft[key] === option"
                      class="i-lucide-check size-4 text-n-slate-11 shrink-0"
                    />
                  </button>
                  <p
                    v-if="!getFilteredDraftCustomFieldOptions(key).length"
                    class="px-3 py-2 text-sm text-n-slate-11"
                  >
                    {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.EMPTY_FOUND') }}
                  </p>
                </div>
              </div>
            </teleport>

            <label
              v-if="getDraftCustomFieldType(key) === 'checkbox'"
              class="inline-flex items-center gap-2 text-sm text-n-slate-12"
            >
              <input
                :checked="Boolean(dealCustomFieldsDraft[key])"
                type="checkbox"
                @change="
                  updateDraftCustomFieldValue(key, $event.target.checked)
                "
              />{{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.ACTIVE') }}</label
            >

            <input
              v-if="
                ![
                  'text',
                  'link',
                  'phone_number',
                  'number',
                  'currency',
                  'date',
                  'list',
                  'checkbox',
                ].includes(getDraftCustomFieldType(key))
              "
              :value="dealCustomFieldsDraft[key] || ''"
              type="text"
              class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
              @input="updateDraftCustomFieldValue(key, $event.target.value)"
            />
          </div>

          <p
            v-if="!dealCustomFieldKeys.length"
            class="text-sm text-center text-n-slate-11 py-6"
          >
            {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.EMPTY') }}
          </p>
        </div>

        <div class="shrink-0 flex items-center justify-between pt-1">
          <button
            type="button"
            class="text-sm text-n-brand"
            @click="openAddDealAttributeModal"
          >
            {{ t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.CREATE_NEW') }}
          </button>
          <div class="flex gap-2">
            <Button
              slate
              faded
              :label="t('DEALS.CONVERSATION.PRODUCTS.CANCEL')"
              @click="closeDealCustomFieldsModal"
            />
            <Button
              :is-loading="customFieldsSaving"
              :label="t('DEALS.CONVERSATION.CUSTOM_ATTRIBUTES.SAVE')"
              @click="saveDealCustomFields"
            />
          </div>
        </div>
      </div>
    </div>
  </woot-modal>

  <AddAttribute
    v-if="showAddDealAttributeModal"
    :on-close="closeAddDealAttributeModal"
    :selected-attribute-model-tab="2"
    hide-attribute-model-selector
  />

  <woot-modal v-model:show="showAddLabelModal" :on-close="closeAddLabelModal">
    <AddLabel @close="closeAddLabelModal" />
  </woot-modal>

  <DealModal
    v-if="showCreateDealModal"
    :show="showCreateDealModal"
    :deal="selectedDealForForm"
    :pipeline-id="modalPipelineId"
    :stages="modalStages"
    :initial-values="isEditingMode ? {} : createDealInitialValues"
    @close="closeCreateDealModal"
    @submit="submitDealFromModal"
  />

  <woot-modal v-model:show="showLoseDealModal" :on-close="closeLoseDealModal">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="t('DEALS.CONVERSATION.LOSS_MODAL.TITLE')"
        :header-content="t('DEALS.CONVERSATION.LOSS_MODAL.DESC')"
      />
      <div class="modal-content w-full !pt-8 space-y-4">
        <div class="space-y-2">
          <label class="text-sm font-medium text-n-slate-12 block">
            {{ t('DEALS.CONVERSATION.LOSS_MODAL.REASON_LABEL') }}
          </label>
          <ComboBox
            v-model="selectedLossReasonId"
            :options="lossReasonOptions"
            :placeholder="t('DEALS.CONVERSATION.LOSS_MODAL.REASON_PLACEHOLDER')"
            class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
          />
        </div>
        <div class="space-y-2">
          <label class="text-sm font-medium text-n-slate-12 block">
            {{ t('DEALS.CONVERSATION.LOSS_MODAL.JUSTIFICATION_LABEL') }}
          </label>
          <textarea
            v-model="lossJustification"
            rows="3"
            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-solid-1 text-sm"
            :placeholder="
              selectedLossReason?.requires_justification
                ? t('DEALS.CONVERSATION.LOSS_MODAL.JUSTIFICATION_REQUIRED')
                : t('DEALS.CONVERSATION.LOSS_MODAL.JUSTIFICATION_OPTIONAL')
            "
          />
        </div>
        <div class="flex justify-end gap-2 pt-2">
          <Button
            slate
            faded
            :label="t('DEALS.CONVERSATION.LOSS_MODAL.CANCEL')"
            @click="closeLoseDealModal"
          />
          <Button
            :is-loading="loseDealLoading"
            :label="t('DEALS.CONVERSATION.LOSS_MODAL.CONFIRM')"
            @click="confirmLoseDeal"
          />
        </div>
      </div>
    </div>
  </woot-modal>

  <teleport to="body">
    <div
      v-if="showConversationDock"
      class="contact-conversation-dock fixed right-4 bottom-4 z-[9000] w-[980px] max-w-[calc(100vw-1.5rem)] h-[78vh] max-h-[860px] rounded-xl border border-n-weak bg-n-solid-1 shadow-2xl flex flex-col overflow-visible"
      @click.capture="handleDockClickCapture"
    >
      <div
        class="relative z-[220] h-12 px-3 border-b border-n-weak flex items-center justify-between gap-2"
      >
        <div class="min-w-0">
          <p class="text-sm font-semibold text-n-slate-12 truncate">
            {{ t('CONTACT_DEALS.DOCK_CONVERSATION_PREFIX')
            }}{{ dockConversationId }}
          </p>
          <p class="text-xs text-n-slate-11 truncate">
            {{ t('CONTACT_DEALS.DOCK_NATIVE_MODE') }}
          </p>
        </div>
        <div class="inline-flex items-center gap-1">
          <button
            type="button"
            class="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-n-alpha-2 text-n-slate-11 pointer-events-auto"
            :title="t('CONVERSATION.SIDEBAR.CONTACT')"
            @click="handleDockContactClick"
          >
            <span class="i-ph-user-bold size-4" />
          </button>
          <button
            type="button"
            class="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-n-alpha-2 text-n-slate-11 pointer-events-auto"
            @click="handleDockCloseClick"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>
      </div>

      <div class="flex-1 min-h-0 h-full overflow-visible">
        <section class="flex w-full h-full min-w-0 overflow-visible">
          <ConversationBox
            v-if="dockConversationId"
            class="h-full min-w-0 flex-1"
            :inbox-id="dockConversationInboxId"
            is-inbox-view
            :is-on-expanded-layout="false"
          />
          <aside
            v-if="isDockSidebarOpen && currentDockChat?.id"
            class="contact-conversation-dock__sidebar w-[340px] min-w-[340px] h-full border-l border-n-weak bg-n-surface-2 overflow-auto"
          >
            <ContactPanel
              :conversation-id="currentDockChat.id"
              :inbox-id="currentDockChat.inbox_id"
              :fetch-contact-details="false"
              :show-header="false"
            />
          </aside>
        </section>
      </div>
    </div>
  </teleport>

  <ConfirmationModal
    ref="confirmDealDeleteModal"
    :title="t('CONTACT_DEALS.DELETE_CONFIRM_TITLE')"
    :description="t('CONTACT_DEALS.DELETE_CONFIRM_DESC')"
    :confirm-label="t('CONTACT_DEALS.DELETE_CONFIRM_BTN')"
    :cancel-label="t('CONTACT_DEALS.DELETE_CANCEL_BTN')"
  />
</template>

<style scoped>
.contact-conversation-dock :deep(.conversation-header .actions--container) {
  position: relative;
  z-index: 120;
}

.contact-conversation-dock
  :deep(.conversation-header .actions--container [class*='top-full']) {
  left: auto !important;
  right: calc(100% + 0.25rem) !important;
  top: 100% !important;
  z-index: 200 !important;
}

.contact-conversation-dock
  :deep(.conversation-header .actions--container .bg-n-alpha-3.absolute) {
  left: auto !important;
  right: calc(100% + 0.25rem) !important;
  top: 100% !important;
  z-index: 200 !important;
}

.contact-conversation-dock :deep(.actions--container .absolute) {
  left: auto !important;
  right: calc(100% + 0.25rem) !important;
  top: 100% !important;
  z-index: 200 !important;
}

.contact-conversation-dock
  :deep(.conversation-header .actions--container .group) {
  z-index: 140;
}

.contact-conversation-dock :deep(.contact--panel) {
  height: 100%;
}

.contact-conversation-dock :deep(.contact--profile a[href*='/contacts/']) {
  pointer-events: none;
}

.contact-conversation-dock__sidebar {
  z-index: 60;
}
</style>
